// src/components/quizzes/BonusContinentsandOceans.js
//
// Continents & oceans bonus round: catch the name, or the place that matches the clue.
// Runs on the shared bonus-round engine (kit/BonusShell.js) — the
// BonusDiamonds look, bombs, bonus stars, clocks, combos, a new question
// every few right answers, and 🏁 End game. Same contract as before:
// BonusRouter renders <ThisGame onBonusComplete questions placeholderImage />.

import React from "react";
import BonusShell from "./kit/BonusShell";

const SPAWN_MS = 480;
const MIN_TARGETS_ONSCREEN = 2;
const TARGET_SPEED_SLOWDOWN = 0.92;

/** ======= CONTINENTS & OCEANS ======= */
const UNITS = [
  // Continents
  { kind: "Continent", name: "Africa", short: "Africa", clue: "Sahara Desert & Nile", emoji: "🦁" },
  { kind: "Continent", name: "Antarctica", short: "Antarctica", clue: "Icy land at South Pole", emoji: "🐧" },
  { kind: "Continent", name: "Asia", short: "Asia", clue: "Largest continent", emoji: "🕌" },
  { kind: "Continent", name: "Europe", short: "Europe", clue: "Peninsulas & the Alps", emoji: "🏰" },
  { kind: "Continent", name: "North America", short: "North America", clue: "Rockies & Great Lakes", emoji: "🦬" },
  { kind: "Continent", name: "South America", short: "South America", clue: "Amazon & Andes", emoji: "🦜" },
  { kind: "Continent", name: "Oceania", short: "Oceania", clue: "Australia & Pacific islands", emoji: "🦘" },

  // Oceans
  { kind: "Ocean", name: "Pacific Ocean", short: "Pacific Ocean", clue: "Largest & deepest ocean", emoji: "🌊" },
  { kind: "Ocean", name: "Atlantic Ocean", short: "Atlantic Ocean", clue: "Between Americas & Europe/Africa", emoji: "⚓️" },
  { kind: "Ocean", name: "Indian Ocean", short: "Indian Ocean", clue: "South of Asia", emoji: "🧭" },
  { kind: "Ocean", name: "Arctic Ocean", short: "Arctic Ocean", clue: "Around the North Pole", emoji: "🧊" },
  { kind: "Ocean", name: "Southern Ocean", short: "Southern Ocean", clue: "Around Antarctica", emoji: "❄️" },
];

// helpers
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

// choose prompt style each round
const makePrompt = () => {
  const mode = Math.random() < 0.55 ? "name" : "clue";
  const unit = pick(UNITS);
  return { mode, unit }; // unit is an object from UNITS
};

const shouldSpawnCorrectBalanced = (target, items) => {
  const correct = items.filter((it) => it.unit.name === target.name).length;
  const wrong = items.length - correct;
  if (correct < MIN_TARGETS_ONSCREEN) return true;
  if (correct < wrong) return true;
  if (wrong < correct) return false;
  return Math.random() < 0.5;
};

const newPrompt = (prev) => {
  let next = makePrompt();
  for (let i = 0; i < 8 && prev && next.unit.name === prev.unit.name; i += 1) next = makePrompt();
  return next;
};

const PlacesBonus = (props) => (
  <BonusShell
    {...props}
    title="Continents & Oceans!"
    emoji="🌍"
    sub="Catch the name shown at the top — or the place that matches the clue."
    intro="Continents and oceans! Catch the name shown at the top, or the place that matches the clue. Each one is worth five points. Watch out for bombs! Press Start when you are ready."
    rules={[
      { icon: "✅", text: "The right place", pts: "+5" },
      { icon: "❌", text: "A different place", pts: "-3" },
    ]}
    init={newPrompt}
    prompt={(p) =>
      p.mode === "name"
        ? { big: p.unit.short, small: "Catch this one!", say: `Catch ${p.unit.short}.` }
        : { big: p.unit.clue, small: "Which place is this? Catch it!", say: `Which one is: ${p.unit.clue}?` }
    }
    spawn={(p, onScreen) => {
      const want = shouldSpawnCorrectBalanced(p.unit, onScreen.map((x) => ({ unit: x.value })));
      return want ? p.unit : pick(UNITS.filter((u) => u.name !== p.unit.name));
    }}
    check={(p, v) => v.name === p.unit.name}
    label={(v) => (
      <span>
        <span style={{ fontSize: 26 }}>{v.emoji}</span> {v.short}
      </span>
    )}
    shape="chip"
    itemSize={72}
    badPoints={3}
    newEvery={3}
    spawnMs={SPAWN_MS}
    slowGood={TARGET_SPEED_SLOWDOWN}
    caughtLabel="Places"
  />
);

export default PlacesBonus;
